/**
 * @fileoverview Window Manager Context
 * @description Global registry of minimizable windows (dialogs + route pages).
 * State is persisted to localStorage so minimized windows and z-order survive
 * page refreshes. Only `route` windows are rehydrated as restorable chips on
 * load; transient `dialog` windows are pruned on hydrate (their host component
 * isn't mounted yet) but their last minimized state is replayed when the same
 * id registers again in this session.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type WindowKind = 'dialog' | 'route';

export interface ManagedWindow {
  id: string;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  isMinimized: boolean;
  zIndex: number;
  kind: WindowKind;
  /** For route windows: the pathname to navigate back to on restore. */
  path?: string;
}

interface RegisterInput {
  id: string;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  kind?: WindowKind;
  path?: string;
  /** Called when the user closes the window from the taskbar X. */
  onClose?: () => void;
}

interface WindowManagerContextValue {
  windows: ManagedWindow[];
  register: (w: RegisterInput) => void;
  unregister: (id: string) => void;
  /** User-initiated close: fires onClose handler, then unregisters. */
  requestClose: (id: string) => void;
  /** Close every open window (dialogs trigger their onClose, routes are dropped). */
  closeAll: () => void;
  minimize: (id: string) => void;
  restore: (id: string) => void;
  bringToFront: (id: string) => number;
  isMinimized: (id: string) => boolean;
}

const WindowManagerContext = createContext<WindowManagerContextValue | null>(null);

const STORAGE_KEY = 'wm.state.v1';

interface PersistedState {
  windows: Array<Omit<ManagedWindow, 'icon'>>;
  zCounter: number;
}

function loadPersisted(): PersistedState {
  if (typeof window === 'undefined') return { windows: [], zCounter: 50 };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { windows: [], zCounter: 50 };
    const parsed = JSON.parse(raw) as PersistedState;
    return {
      windows: Array.isArray(parsed.windows) ? parsed.windows : [],
      zCounter: typeof parsed.zCounter === 'number' ? parsed.zCounter : 50,
    };
  } catch {
    return { windows: [], zCounter: 50 };
  }
}

export function WindowManagerProvider({ children }: { children: ReactNode }) {
  const persisted = useRef(loadPersisted());

  // Initial state: keep route windows so the taskbar reappears after refresh.
  // Dialog windows can't be restored without their host so they're dropped
  // from the visible list but retained in `minimizedMemory` for replay this
  // session if their host registers again.
  const initialRoutes: ManagedWindow[] = persisted.current.windows
    .filter((w) => w.kind === 'route')
    .map((w) => ({ ...w, icon: undefined }));

  const minimizedMemory = useRef<Set<string>>(
    new Set(persisted.current.windows.filter((w) => w.isMinimized).map((w) => w.id)),
  );

  const [windows, setWindows] = useState<ManagedWindow[]>(initialRoutes);
  const zCounter = useRef(Math.max(50, persisted.current.zCounter));

  // Persist on every change (debounced via rAF).
  useEffect(() => {
    let raf = 0;
    raf = requestAnimationFrame(() => {
      try {
        const data: PersistedState = {
          windows: windows.map(({ icon: _icon, ...rest }) => rest),
          zCounter: zCounter.current,
        };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch {
        /* quota or privacy mode — ignore */
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [windows]);

  // Store onClose handlers outside React state so they don't trigger re-renders.
  const closeHandlers = useRef<Map<string, () => void>>(new Map());

  const register = useCallback((w: RegisterInput) => {
    const kind: WindowKind = w.kind ?? 'dialog';
    if (w.onClose) closeHandlers.current.set(w.id, w.onClose);
    setWindows((prev) => {
      const existing = prev.find((x) => x.id === w.id);
      if (existing) {
        if (
          existing.title === w.title &&
          existing.subtitle === w.subtitle &&
          existing.kind === kind
        ) {
          return prev;
        }
        return prev.map((x) =>
          x.id === w.id
            ? { ...x, title: w.title, subtitle: w.subtitle, icon: w.icon, kind, path: w.path ?? x.path }
            : x,
        );
      }
      zCounter.current += 1;
      const wasMinimized = minimizedMemory.current.has(w.id);
      return [
        ...prev,
        {
          id: w.id,
          title: w.title,
          subtitle: w.subtitle,
          icon: w.icon,
          kind,
          path: w.path,
          isMinimized: wasMinimized,
          zIndex: zCounter.current,
        },
      ];
    });
  }, []);

  const unregister = useCallback((id: string) => {
    closeHandlers.current.delete(id);
    minimizedMemory.current.delete(id);
    setWindows((prev) => prev.filter((w) => w.id !== id));
  }, []);


  const requestClose = useCallback((id: string) => {
    const handler = closeHandlers.current.get(id);
    if (handler) {
      // Let the host component drive the close (so its open state stays in sync).
      // The host's cleanup effect will call unregister.
      closeHandlers.current.delete(id);
      try { handler(); } catch { /* noop */ }
      return;
    }
    setWindows((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const closeAll = useCallback(() => {
    // Snapshot handlers first; firing them mutates the map.
    const handlers = Array.from(closeHandlers.current.entries());
    closeHandlers.current.clear();
    for (const [, h] of handlers) {
      try { h(); } catch { /* noop */ }
    }
    setWindows([]);
  }, []);

  const minimize = useCallback((id: string) => {
    minimizedMemory.current.add(id);
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, isMinimized: true } : w)));
  }, []);

  const restore = useCallback((id: string) => {
    minimizedMemory.current.delete(id);
    zCounter.current += 1;
    const z = zCounter.current;
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, isMinimized: false, zIndex: z } : w)));
  }, []);

  const bringToFront = useCallback((id: string) => {
    zCounter.current += 1;
    const z = zCounter.current;
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, zIndex: z } : w)));
    return z;
  }, []);

  const isMinimized = useCallback(
    (id: string) => windows.find((w) => w.id === id)?.isMinimized ?? false,
    [windows],
  );

  const value = useMemo(
    () => ({ windows, register, unregister, requestClose, closeAll, minimize, restore, bringToFront, isMinimized }),
    [windows, register, unregister, requestClose, closeAll, minimize, restore, bringToFront, isMinimized],
  );

  return <WindowManagerContext.Provider value={value}>{children}</WindowManagerContext.Provider>;
}

export function useWindowManager() {
  const ctx = useContext(WindowManagerContext);
  if (!ctx) throw new Error('useWindowManager must be used within WindowManagerProvider');
  return ctx;
}

/** Same as useWindowManager but returns null if no provider is mounted. */
export function useOptionalWindowManager() {
  return useContext(WindowManagerContext);
}
